// BeeNexa Hive — the app catalog.
//
// This is the single source of marketplace metadata. Each entry layers product
// info (name, tagline, category, icon) over an existing route.
// Adding a future app = one entry here (+ its route in routes/index.js + its
// server role). The router and permission system are untouched.
//
// Fields:
//   id       — for LIVE apps this MUST equal the route id in routes/index.js and
//              the string stored in User.routes (that's how "installed" is known).
//              Coming-soon apps use a `cs-` id that intentionally has no route.
//   name     — display name
//   tagline  — one-line description (end-user language, active voice)
//   category — a category id from categories.js
//   scope    — 'standalone' (a marketplace app) | 'personal' (a per-user feature
//              such as Document Center / Tasks / Profit Tracker / Investor Tracker)
//              | 'system' (admin/settings — not shown as a store card)
//   icon     — lucide-react icon name
//   accent   — gradient key in theme/tokens.js (defaults to category)
//   status   — 'live' | 'coming-soon'
//   path     — route path to open (live apps only)
//   popular  — appears in the "Popular Apps" rail (with popularOrder)

export const APP_REGISTRY = [
  // ── Business ──────────────────────────────────────────────────────────
  {
    id: 'business-tracker',
    name: 'Business Tracker',
    tagline: 'Track recurring services, bills and profit in real time.',
    category: 'business',
    scope: 'standalone',
    icon: 'TrendingUp',
    status: 'live',
    path: '/business-tracker',
    popular: true,
    popularOrder: 1,
    description:
      'Manage every recurring business service and operational account from one place — electricity, insurance, alarm, pest control, mortgage/lease, tenant rent, sales & property tax, licenses and maintenance.',
  },

  // ── Restaurant ────────────────────────────────────────────────────────
  {
    id: 'recipe-cost',
    name: 'Recipe Cost Calculator',
    tagline: 'Cost recipes, set menu prices and watch food cost.',
    category: 'restaurant',
    scope: 'standalone',
    icon: 'ChefHat',
    status: 'live',
    path: '/recipe-cost',
    popular: true,
    popularOrder: 3,
    description:
      'Build recipes and ingredients, calculate true food cost with waste (merma), get suggested pricing by margin, and analyze menu performance.',
  },
  {
    id: 'cs-restaurant-manager',
    name: 'Restaurant Manager',
    tagline: 'Manage orders, inventory, staff and more.',
    category: 'restaurant',
    scope: 'standalone',
    icon: 'UtensilsCrossed',
    status: 'coming-soon',
    popular: true,
    popularOrder: 2,
    description: 'A full front-of-house and back-of-house manager for restaurants.',
  },
  {
    id: 'cs-weekly-report',
    name: 'Weekly Report',
    tagline: 'Daily & weekly sales, labor, expenses and profit.',
    category: 'restaurant',
    scope: 'standalone',
    icon: 'BarChart3',
    status: 'coming-soon',
    description:
      'Track daily and weekly sales, labor cost, inventory, expenses, profit summary and operational KPIs.',
  },

  // ── Booking ───────────────────────────────────────────────────────────
  {
    id: 'cs-booking-system',
    name: 'Booking System',
    tagline: 'Appointments, scheduling and reminders.',
    category: 'booking',
    scope: 'standalone',
    icon: 'CalendarCheck',
    status: 'coming-soon',
    popular: true,
    popularOrder: 4,
    description: 'Let customers book time, manage your calendar and send reminders.',
  },

  // ── Real Estate ───────────────────────────────────────────────────────
  {
    id: 'real-estate',
    name: 'House Projects',
    tagline: 'Run rehab & flip projects, partners and expenses.',
    category: 'realEstate',
    scope: 'standalone',
    icon: 'Hammer',
    status: 'live',
    path: '/real-estate',
    description:
      'Track houses, partners, contractors and expenses across your rehab and flip projects.',
  },
  {
    id: 'cs-portfolio',
    name: 'Real Estate Portfolio',
    tagline: 'Your whole property portfolio at a glance.',
    category: 'realEstate',
    scope: 'standalone',
    icon: 'Building2',
    status: 'coming-soon',
    description: 'A single view of every property you own and its performance.',
  },
  {
    id: 'cs-re-cashflow',
    name: 'Real Estate Cash Flow',
    tagline: 'Rental income, expenses and monthly cash flow.',
    category: 'realEstate',
    scope: 'standalone',
    icon: 'Coins',
    status: 'coming-soon',
    description: 'Model rental performance and cash flow across your portfolio.',
  },

  // ── Finance ───────────────────────────────────────────────────────────
  {
    id: 'loans',
    name: 'Loans',
    tagline: 'Borrowers, transactions and loan performance.',
    category: 'finance',
    scope: 'standalone',
    icon: 'Landmark',
    status: 'live',
    path: '/loans',
    description: 'Track customer loans, borrowers and repayment transactions.',
  },

  // ── Event Planning ────────────────────────────────────────────────────
  {
    id: 'cs-event-planner',
    name: 'Event Planner',
    tagline: 'Guests, budget, vendors and timelines.',
    category: 'event',
    scope: 'standalone',
    icon: 'PartyPopper',
    status: 'coming-soon',
    description:
      'Plan events end to end — guest manager, budget planner, vendor timeline, seating and photo checklist.',
  },

  // ── Personal / per-user features ──────────────────────────────────────
  // These are NOT standalone marketplace apps; they belong to specific users
  // (granted per-user in Manage Employee). They appear in "My Apps → Personal".
  {
    id: 'document-center',
    name: 'Document Center',
    tagline: 'Your files, folders and documents.',
    category: 'personal',
    scope: 'personal',
    icon: 'FolderOpen',
    status: 'live',
    path: '/document-center',
    description: 'A shared document center with folders, upload, preview and recycle bin.',
  },
  {
    id: 'todo',
    name: 'Tasks',
    tagline: 'To-dos, assignments and reminders.',
    category: 'personal',
    scope: 'personal',
    icon: 'ListChecks',
    status: 'live',
    path: '/task',
    description: 'A simple task list with cards, attachments and due dates.',
  },
  {
    id: 'profit-tracker',
    name: 'Profit Tracker',
    tagline: 'Weekly profit across your locations.',
    category: 'personal',
    scope: 'personal',
    icon: 'LineChart',
    status: 'live',
    entitlement: 'admin-only',
    path: '/profit-tracker',
    description: 'Track weekly profit, ATM records and location performance.',
  },
  {
    id: 'investor-tracker',
    name: 'Investor Tracker',
    tagline: 'Investors, participation and payouts.',
    category: 'personal',
    scope: 'personal',
    icon: 'PiggyBank',
    status: 'live',
    path: '/investor-tracker',
    description: 'Manage investors, locations, expenses, payments and contractor quotes.',
  },
];

// Fast lookup by id.
export const APP_BY_ID = APP_REGISTRY.reduce((acc, a) => {
  acc[a.id] = a;
  return acc;
}, {});

export const appById = (id) => APP_BY_ID[id];
